import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
  InputAdornment,
  Tooltip
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import PublicIcon from "@mui/icons-material/Public";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import GroupedSelect from "../../utils/GroupedSelect";

const extractList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
};

const normalizeTagName = (value = "") =>
  String(value).trim().replace(/\s+/g, " ");

const DisclosureFormDialog = ({
  open,
  mode,
  submitting,
  initialValues,
  listDisclosureTags,
  createDisclosureTag,
  getCompanyList,
  onClose,
  onSubmit,
  onError
}) => {
  const theme = useTheme();

  const [title, setTitle] = useState("");
  const [statementText, setStatementText] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [companyId, setCompanyId] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState("");

  const [companies, setCompanies] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.c_id,
        label: company.name
          ? `${company.name}${company.uen ? ` (${company.uen})` : ""}`
          : company.c_id
      })),
    [companies]
  );

  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        id: tag.t_id,
        name: tag.name,
        is_system: Boolean(tag.is_system)
      })),
    [tags]
  );

  const displayTags = useMemo(() => {
    const searchTerm = tagInput.trim().toLowerCase();

    if (searchTerm) {
      // When searching, show matching tags
      return tagOptions.filter((tag) =>
        tag.name.toLowerCase().includes(searchTerm)
      );
    }

    // When not searching, show all tags with selected ones first
    const selectedIds = new Set(selectedTags.map((t) => t.t_id));
    const selected = tagOptions.filter((tag) => selectedIds.has(tag.id));
    const unselected = tagOptions.filter((tag) => !selectedIds.has(tag.id));

    return [...selected, ...unselected];
  }, [tagInput, tagOptions, selectedTags]);

  const loadMeta = async () => {
    setLoadingMeta(true);
    try {
      const [companyRes, tagRes] = await Promise.all([
        getCompanyList("active"),
        listDisclosureTags()
      ]);
      setCompanies(extractList(companyRes));
      setTags(extractList(tagRes));
    } catch (error) {
      onError(error, error?.message || "Failed to load disclosure form data");
      setCompanies([]);
      setTags([]);
    } finally {
      setLoadingMeta(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadMeta();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTitle(initialValues?.title || "");
    setStatementText(initialValues?.statement_text || "");
    setIsActive(initialValues?.is_active ?? true);
    setCompanyId(initialValues?.c_id || "");
    setSelectedTags(initialValues?.disclosure_tags || []);
    setTagInput("");
  }, [open, initialValues]);

  const createTagFromInput = async (
    rawValue = tagInput,
    { reportError = true, select = true } = {}
  ) => {
    const normalized = normalizeTagName(rawValue);
    if (!normalized) return null;

    const existing =
      tagOptions.find(
        (tag) => tag.name.toLowerCase() === normalized.toLowerCase()
      ) || null;
    if (existing) {
      const isAlreadySelected = selectedTags.some(
        (t) => t.t_id === existing.id
      );
      if (select && !isAlreadySelected) {
        setSelectedTags((prev) => [
          ...prev,
          {
            t_id: existing.id,
            name: existing.name,
            is_system: existing.is_system
          }
        ]);
      }
      setTagInput("");
      return {
        t_id: existing.id,
        name: existing.name,
        is_system: existing.is_system
      };
    }

    setCreatingTag(true);
    try {
      const res = await createDisclosureTag({
        name: normalized,
        is_system: false
      });
      const created = res?.data || res;
      if (!created?.t_id) {
        throw new Error("Invalid tag response");
      }

      setTags((prev) => {
        const exists = prev.some((tag) => tag.t_id === created.t_id);
        if (exists) return prev;
        return [created, ...prev];
      });

      setSelectedTags((prev) => {
        const exists = prev.some((tag) => tag.t_id === created.t_id);
        if (exists) return prev;
        if (!select) return prev;
        return [
          ...prev,
          {
            t_id: created.t_id,
            name: created.name,
            is_system: Boolean(created.is_system)
          }
        ];
      });
      setTagInput("");
      return {
        t_id: created.t_id,
        name: created.name,
        is_system: Boolean(created.is_system)
      };
    } catch (error) {
      if (reportError) {
        onError(error, error?.message || "Failed to create tag");
        return null;
      }
      throw error;
    } finally {
      setCreatingTag(false);
    }
  };

  const handleSubmit = async () => {
    const normalizedTitle = title.trim();
    const normalizedStatement = statementText.trim();
    if (!normalizedTitle) {
      onError(null, "Title is required");
      return;
    }
    if (!normalizedStatement) {
      onError(null, "Statement is required");
      return;
    }
    try {
      // If user typed a tag but did not press Enter/Create, ensure it is included.
      const pendingTagName = normalizeTagName(tagInput);
      const pendingTag = pendingTagName
        ? await createTagFromInput(pendingTagName, {
            reportError: false,
            select: true
          })
        : null;

      const mergedTags = pendingTag
        ? [...selectedTags, pendingTag]
        : [...selectedTags];

      const tagIds = [
        ...new Set(
          mergedTags
            .map((t) => t.t_id)
            .filter(Boolean)
            .map(String)
        )
      ];

      await onSubmit({
        title: normalizedTitle,
        statement_text: normalizedStatement,
        is_active: Boolean(isActive),
        c_id: companyId || null,
        tag_ids: tagIds
      });
    } catch (error) {
      onError(error, error?.message || "Failed to save disclosure");
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.15)",
          overflow: "visible"
        }
      }}
    >
      {/* 1. 头部：极简风格 */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              p: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              borderRadius: 2
            }}
          >
            <ArticleIcon />
          </Box>
          <Box>
            <Typography variant='h6' fontWeight={700}>
              {mode === "create" ? "Create Disclosure" : "Update Disclosure"}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {mode === "create"
                ? "Create a new Disclosure."
                : "Update the content of this Disclosure."}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size='small'
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "7fr 3fr" },
            gap: 2.5
          }}
        >
          {/* 左侧：核心编辑区 */}
          <Box
            sx={{
              p: 2.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "background.paper"
            }}
          >
            <Stack spacing={2}>
              <TextField
                label='Disclosure Title'
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                variant='filled'
                InputProps={{
                  sx: {
                    borderRadius: 2,
                    bgcolor: "background.default",
                    fontWeight: 600
                  }
                }}
              />

              <TextField
                label='Disclosure Statement'
                value={statementText}
                onChange={(e) => setStatementText(e.target.value)}
                fullWidth
                multiline
                minRows={14}
                variant='filled'
                placeholder='Enter the content here...'
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: "background.default" }
                }}
              />
            </Stack>
          </Box>

          {/* 右侧：侧边栏 */}
          <Stack spacing={2}>
            {/* 卡片1：发布设置 */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3
              }}
            >
              <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1.5 }}>
                Visibility
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(isActive)}
                      onChange={(e) => setIsActive(e.target.checked)}
                      color='success'
                    />
                  }
                  label={
                    <Stack direction='row' spacing={0.75} alignItems='center'>
                      <PublicIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                      >
                        Publish Immediately
                      </Typography>
                    </Stack>
                  }
                  labelTypographyProps={{
                    fontWeight: 600,
                    fontSize: "0.875rem"
                  }}
                />
                <Stack direction='row' spacing={0.75} alignItems='center'>
                  <LockOutlinedIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    fontWeight={600}
                  >
                    Visibility Scope
                  </Typography>
                  <Tooltip
                    arrow
                    title='Selecting a specific Company limits this disclosure to that Company only. Leave as "Everyone can see" to make it available to all Companies.'
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        cursor: "help"
                      }}
                    />
                  </Tooltip>
                </Stack>
                <GroupedSelect
                  value={companyId || ""}
                  onChange={(e) => setCompanyId(e.target.value || "")}
                  options={[
                    {
                      group: "Visibility",
                      value: "",
                      label: "Everyone can see"
                    },
                    ...companyOptions.map((option) => ({
                      group: "Only specific Company",
                      value: option.value,
                      label: option.label
                    }))
                  ]}
                  placeholder='Everyone can see'
                  enableSearch
                  searchPlaceholder='Search Company...'
                  disabled={loadingMeta}
                  sx={{
                    borderRadius: 2,
                    bgcolor:
                      companyId === ""
                        ? (theme) => alpha(theme.palette.common.white, 0.7)
                        : "background.paper"
                  }}
                />
              </Stack>
              {loadingMeta ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 3,
                    bgcolor: (theme) =>
                      alpha(theme.palette.background.paper, 0.72),
                    backdropFilter: "blur(1px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2,
                    pointerEvents: "all"
                  }}
                >
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <CircularProgress color='inherit' size={16} />
                    <Typography variant='caption' color='text.secondary'>
                      Loading...
                    </Typography>
                  </Stack>
                </Box>
              ) : null}
            </Box>

            {/* 卡片2：分类标签 */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                bgcolor: "background.paper",
                height: "100%"
              }}
            >
              <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1.5 }}>
                Classification Tags (Tags)
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='Search or create tags...'
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      (e.key === "Enter" || e.key === ",") &&
                      normalizeTagName(tagInput)
                    ) {
                      e.preventDefault();
                      createTagFromInput(tagInput);
                    }
                  }}
                  variant='outlined'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon color='action' fontSize='small' />
                      </InputAdornment>
                    ),
                    endAdornment:
                      tagInput &&
                      !tagOptions.some(
                        (t) => t.name.toLowerCase() === tagInput.toLowerCase()
                      ) ? (
                        <InputAdornment position='end'>
                          <Button
                            size='small'
                            variant='contained'
                            disabled={creatingTag}
                            onClick={() => createTagFromInput(tagInput)}
                            sx={{ minWidth: "auto", px: 1.2, py: 0.3, mr: 0.3 }}
                          >
                            Create
                          </Button>
                        </InputAdornment>
                      ) : null
                  }}
                />

                <Stack
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600 }}
                  >
                    {tagInput
                      ? `Matching Tags (${displayTags.length})`
                      : `Available Tags (${selectedTags.length} selected)`}
                  </Typography>
                  {!tagInput && selectedTags.length > 0 && (
                    <Button
                      size='small'
                      onClick={() => setSelectedTags([])}
                      sx={{
                        minWidth: "auto",
                        fontSize: "0.75rem",
                        px: 1,
                        py: 0.25,
                        textTransform: "none"
                      }}
                    >
                      Clear all
                    </Button>
                  )}
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    maxHeight: 180,
                    overflowY: "auto",
                    p: 1,
                    bgcolor: (theme) => alpha(theme.palette.grey[50], 0.5),
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  {displayTags.length > 0 ? (
                    displayTags.map((tag) => {
                      const isSelected = selectedTags.some(
                        (t) => t.t_id === tag.id
                      );
                      return (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          size='small'
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTags(
                                selectedTags.filter((t) => t.t_id !== tag.id)
                              );
                            } else {
                              setSelectedTags([
                                ...selectedTags,
                                {
                                  t_id: tag.id,
                                  name: tag.name,
                                  is_system: tag.is_system
                                }
                              ]);
                            }
                          }}
                          onDelete={
                            isSelected
                              ? () =>
                                  setSelectedTags(
                                    selectedTags.filter(
                                      (t) => t.t_id !== tag.id
                                    )
                                  )
                              : undefined
                          }
                          variant={isSelected ? "filled" : "outlined"}
                          color={isSelected ? "primary" : "default"}
                          icon={isSelected ? <CheckIcon /> : undefined}
                          sx={{
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: 1
                            }
                          }}
                        />
                      );
                    })
                  ) : tagInput.trim() ? (
                    <Typography
                      variant='caption'
                      color='text.disabled'
                      sx={{ p: 1, width: "100%", textAlign: "center" }}
                    >
                      No matching tags. Click "Create" to create a new tag.
                    </Typography>
                  ) : (
                    <Typography
                      variant='caption'
                      color='text.disabled'
                      sx={{ p: 1, width: "100%", textAlign: "center" }}
                    >
                      No tags available. Create your first tag above.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: "background.default",
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "flex-end",
          borderRadius: 4,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0
        }}
      >
        <Stack direction='row' spacing={1.5}>
          <Button
            onClick={onClose}
            disabled={submitting}
            variant='text'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={submitting || creatingTag || loadingMeta}
            startIcon={submitting ? null : <CheckCircleIcon />}
            sx={{ px: 3, borderRadius: 2 }}
          >
            {submitting ? "Saving..." : "Save Disclosure"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DisclosureFormDialog;
